import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Loader2, ArrowRight } from "lucide-react";
import { CATEGORIES } from "@/data/catalog";
import { submitQuote } from "@/lib/api";

const empty = { name: "", company: "", email: "", phone: "", city: "", product: "", quantity: "", message: "", artwork_name: "" };

export default function QuoteForm({ defaultProduct = "" }) {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ ...empty, product: defaultProduct });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const urlProduct = searchParams.get("product");
    const urlSize = searchParams.get("size");
    const urlColor = searchParams.get("color");
    const urlQuantity = searchParams.get("quantity");

    if (urlProduct) {
      let details = urlProduct;
      if (urlSize) details += ` (Size: ${urlSize}`;
      if (urlColor) details += `, Color: ${urlColor}`;
      if (urlSize || urlColor) details += ")";
      
      setForm((f) => ({
        ...f,
        product: urlProduct,
        quantity: urlQuantity || "",
        message: details !== urlProduct ? `Product Details: ${details}` : ""
      }));
    }
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitQuote(form);
      setDone(true);
      toast.success("Quote request received! Our team will reach out shortly.");
      setForm({ ...empty });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="rounded-3xl border border-emerald/30 bg-emerald/5 p-10 text-center">
        <h3 className="font-heading text-2xl font-bold text-foreground">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">Your request has been received. A packaging specialist will contact you within one business day.</p>
        <button onClick={() => setDone(false)} className="mt-6 rounded-full bg-navy text-white px-6 py-2.5 font-semibold" data-testid="new-quote-btn">Submit another</button>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-royal placeholder:text-muted-foreground";

  return (
    <form onSubmit={submit} data-testid="quote-form" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="q-name" />
        <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="q-company" />
        <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="q-email" />
        <input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="q-phone" />
        <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="q-city" />
        <select value={form.product} onChange={set("product")} className={inputCls} data-testid="q-product">
          <option value="">Select product</option>
          {CATEGORIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          <option value="Other">Other</option>
        </select>
      </div>
      <input placeholder="Estimated quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="q-quantity" />
      <textarea rows={4} placeholder="Tell us about your requirement" value={form.message} onChange={set("message")} className={inputCls} data-testid="q-message" />
      <label className="flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-4 cursor-pointer hover:bg-secondary/50 transition-colors" data-testid="q-artwork-label">
        <UploadCloud size={20} className="text-royal" />
        <span className="text-sm text-muted-foreground">{form.artwork_name || "Upload artwork (optional)"}</span>
        <input type="file" className="hidden" onChange={(e) => setForm((f) => ({ ...f, artwork_name: e.target.files?.[0]?.name || "" }))} data-testid="q-artwork" />
      </label>
      <button type="submit" disabled={busy} data-testid="q-submit" className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy text-white px-7 py-4 font-semibold hover:bg-royal transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <>Submit Request <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
      </button>
    </form>
  );
}
